import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog, SttFormSelect } from '../all';
import { useInteractionsFiles } from '../../lib/hooks';
import { getErrorMessage } from '../../lib/utils';
import constants from '../../config/constants';

type FileTypeOption = {
    id: string;
    label: string;
    value?: string;
};

type UploadParams = {
    value?: string;
    format?: string;
    name?: string;
    type: string;
    [key: string]: string | number | undefined;
};

type DialogButton = {
    caption: string;
    icon?: React.ReactNode;
    onClick: () => void;
};

type Props = {
    open?: boolean;
    types?: string[];
    uploadParams?: Record<string, unknown>;
    onClose?: () => void;
    onUploaded?: (response: unknown) => void;
    legacyMode?: boolean;
};

export function UploadFileDialog({
    open = false,
    types = [],
    uploadParams = {},
    onClose,
    onUploaded,
    legacyMode = false,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { uploadFile, uploadFileLegacyV6 } = useInteractionsFiles();
    const inputFile = useRef<HTMLInputElement>(null);
    const [selectedType, setSelectedType] = useState<string>();
    const [uploading, setUploading] = useState(false);

    const isUserPhotoUpload = selectedType === 'USER_PHOTO';

    // Use first type as key to avoid infinite loops from array reference changes
    const firstType = types[0];
    useEffect(() => {
        if (firstType) {
            setSelectedType(firstType);
        }
    }, [firstType]);

    const openSelectFile = useCallback(() => {
        inputFile.current?.click();
    }, []);

    const getFileTypeLabel = useCallback(
        (label?: string): string => {
            if (!label) return '';
            const match = label.match(/^\[\[ft\.(.+)\]\]$/);
            if (match?.[1]) {
                return t(`FT.${match[1].toUpperCase()}`);
            }
            return label;
        },
        [t],
    );

    const getFileTypeOptions = useCallback((): FileTypeOption[] => {
        const contextOptions: FileTypeOption[] = cxt?.constants?.fileTypes
            ? cxt.constants.fileTypes.filter((ft: any) => types.includes(ft.id))
            : [];
        const constantOptions = constants.fileTypes.filter((ft: any) =>
            types.includes(ft.id),
        );
        const options = [...contextOptions, ...constantOptions];
        return options.map((ft: any) => ({
            ...ft,
            label: getFileTypeLabel(ft.label || ft.id),
        }));
    }, [cxt?.constants?.fileTypes, getFileTypeLabel, types]);

    const handleUploadSuccess = useCallback(
        (response: unknown) => {
            setUploading(false);
            cxt?.showMessage('S', t('messages.uploaded'));
            onUploaded?.(response);
        },
        [cxt, onUploaded, t],
    );

    const handleUploadError = useCallback(
        (error?: unknown) => {
            setUploading(false);
            // 869df3wdd: surface the backend's specific error message (e.g.
            // "Tipo de archivo no compatible" for an unsupported .docx) instead
            // of the generic toast. Fall back to the generic message only when
            // no meaningful message is available.
            const message = error ? getErrorMessage(error) : '';
            cxt?.showMessage(
                'E',
                message && message !== 'Error' ? message : t('messages.error'),
            );
        },
        [cxt, t],
    );

    const onFileSelected = useCallback(
        (files: FileList | null) => {
            if (!files || files.length === 0) return;

            const file = files[0]!;

            if (legacyMode) {
                const params: UploadParams = {
                    value: file.name,
                    format: file.type,
                    type: selectedType!,
                    ...uploadParams,
                };

                file.arrayBuffer()
                    .then((buffer) => {
                        setUploading(true);
                        return uploadFileLegacyV6(params, buffer);
                    })
                    .then(handleUploadSuccess)
                    .catch(handleUploadError);
            } else {
                // V7 API expects 'name' field (format is extracted from file.mimetype on backend)
                const params: UploadParams = {
                    name: file.name,
                    type: selectedType!,
                    ...uploadParams,
                };

                setUploading(true);
                uploadFile(params, file)
                    .then(handleUploadSuccess)
                    .catch(handleUploadError);
            }
        },
        [
            handleUploadError,
            handleUploadSuccess,
            legacyMode,
            selectedType,
            uploadFile,
            uploadFileLegacyV6,
            uploadParams,
        ],
    );

    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onFileSelected(event.target.files);
        },
        [onFileSelected],
    );

    const handleTypeChange = useCallback(({ value }: { value: unknown }) => {
        setSelectedType(value as string);
    }, []);

    const buttons: DialogButton[] = [
        {
            caption: t('file.selectFile'),
            icon: <AttachFileIcon />,
            onClick: openSelectFile,
        },
    ];

    return (
        <>
            <input
                type="file"
                id="file"
                ref={inputFile}
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />

            <SttDialog
                title={t('file.uploadFile')}
                open={open}
                onClose={onClose}
                content={
                    <>
                        {types.length === 1 ? (
                            <Box>
                                {types[0] === 'USER_PHOTO' ? (
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                        >
                                            {t(
                                                'file.userPhotoUploadDescription',
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t('file.userPhotoUploadFormats')}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t('file.userPhotoUploadMaxSize')}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        {t('labels.type')}:{' '}
                                        {cxt?.constants?.fileTypes &&
                                            getFileTypeLabel(
                                                cxt.constants.fileTypes.find(
                                                    (ft: FileTypeOption) =>
                                                        ft.id === types[0],
                                                )?.label,
                                            )}
                                    </>
                                )}
                            </Box>
                        ) : (
                            <SttFormSelect
                                grid
                                md={12}
                                sm={12}
                                xs={12}
                                caption={t('labels.type')}
                                defVal={selectedType}
                                onChange={handleTypeChange}
                                options={getFileTypeOptions()}
                            />
                        )}

                        {uploading && (
                            <Box mt={3}>
                                <LinearProgress />
                                {isUserPhotoUpload && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1, textAlign: 'center' }}
                                    >
                                        {t('file.uploadingUserPhoto')}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </>
                }
                buttons={
                    buttons as React.ComponentProps<typeof SttDialog>['buttons']
                }
            />
        </>
    );
}
